import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { forkJoin, Observable, of, lastValueFrom } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ExchangeService } from 'projects/shared/services/exchange.service';
import { WebSettingService } from 'projects/shared/services/web-setting.service';
import { AuthService } from 'projects/shared/services/auth.service';
import { StoreChangesService } from 'projects/shared/services/store-changes.service';
import { IWebSetting } from 'projects/shared/models/interfaces/IWebSetting';
import { IUser } from 'projects/shared/models/interfaces/user.model';

export interface AppInitData {
  exchange: any;
  webSetting: IWebSetting | null;
  user: { success: boolean; response: IUser | null };
}

/**
 * AppInitializerService
 * 
 * يضمن تحميل البيانات الأساسية (exchange, webSetting, user) قبل رندرة الصفحة
 * في SSR والمتصفح على حد سواء.
 * 
 * يتم استدعاؤه عن طريق APP_INITIALIZER في app.module.ts
 */
@Injectable({ providedIn: 'root' })
export class AppInitializerService {
  private _initData: AppInitData | null = null;
  private _initPromise: Promise<AppInitData> | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private exchangeService: ExchangeService,
    private webSettingService: WebSettingService,
    private authService: AuthService,
    private storeChangesService: StoreChangesService
  ) {}

  /**
   * Get cached init data (if already loaded)
   */
  get initData(): AppInitData | null {
    return this._initData;
  }

  /**
   * Initialize app - fetch all critical data before app starts
   * This runs ONCE at app bootstrap (before any component renders)
   */
  async initialize(): Promise<AppInitData> {
    // Return cached promise if already initializing
    if (this._initPromise) {
      return this._initPromise;
    }

    // Return cached data if already initialized
    if (this._initData) {
      return Promise.resolve(this._initData);
    }

    console.log('🚀 [APP_INITIALIZER] Starting app initialization...');
    const startTime = Date.now();

    this._initPromise = lastValueFrom(
      forkJoin({
        exchange: this.exchangeService.getUserExchange().pipe(
          tap(() => console.log('✅ Exchange loaded')),
          catchError((err) => {
            console.error('❌ Failed to load exchange:', err);
            return of(null);
          })
        ),
        webSetting: this.webSettingService.getWebsetting().pipe(
          tap(() => console.log('✅ WebSetting loaded')),
          catchError((err) => {
            console.error('❌ Failed to load webSetting:', err);
            return of(null);
          })
        ),
        user: this.authService.validate().pipe(
          map((response) => {
            console.log('✅ User validated');
            return { success: true, response };
          }),
          catchError((err) => {
            console.log('ℹ️ User not authenticated (expected on first visit)');
            return of({ success: false, response: null });
          })
        ),
      }).pipe(
        tap((data) => {
          // Store in service for later access
          this._initData = data;

          // Update global stores
          if (data.exchange) {
            this.storeChangesService.setExchange(data.exchange);
          }

          if (data.webSetting) {
            this.storeChangesService.setWalletShow(data.webSetting.hide_wallet);
          }

          if (data.user.success && data.user.response) {
            this.storeChangesService.setUser(data.user.response);
            this.authService.updateUserShop(data.user.response);
          }

          const duration = Date.now() - startTime;
          console.log(`✅ [APP_INITIALIZER] Completed in ${duration}ms`);
        }),
        catchError((err) => {
          console.error('❌ [APP_INITIALIZER] Critical error:', err);
          // Return empty data on critical failure (don't block app)
          return of({
            exchange: null,
            webSetting: null,
            user: { success: false, response: null },
          } as AppInitData);
        })
      )
    );

    return this._initPromise;
  }
}

/**
 * Factory function for APP_INITIALIZER
 * Angular will call this and wait for the Promise to resolve before bootstrapping
 */
export function appInitializerFactory(
  appInitializer: AppInitializerService
): () => Promise<AppInitData> {
  return () => appInitializer.initialize();
}
